import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { friendlyErrorMessage } from "@/lib/friendlyError";
import { clearAuthAttempt } from "@/lib/authAttempt";
import { supabase } from "@/lib/supabase";

type AccountActionsProps = {
  placement?: "header" | "library" | "voice";
};

export default function AccountActions({ placement = "header" }: AccountActionsProps) {
  const { signOut, session } = useAuth();
  const [, navigate] = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  async function handleSignOut() {
    setError(null);
    setIsSigningOut(true);
    try {
      await signOut();
      navigate("/", { replace: true });
    } catch (signOutError) {
      setError(friendlyErrorMessage(signOutError, "account", "We couldn’t sign you out. Please try again."));
    } finally {
      setIsSigningOut(false);
    }
  }

  async function handleDeleteAccount() {
    if (deleteConfirmation !== "DELETE" || !supabase) return;
    setError(null);
    setIsDeletingAccount(true);
    try {
      const { data, error: deleteError } = await supabase.functions.invoke("delete-account");
      if (deleteError) throw deleteError;
      if (data?.success !== true) throw new Error("The account deletion did not complete.");
      // The server has removed the Auth user. Clear local session state without
      // requiring a remote logout using the now-deleted refresh token.
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* Completion remains true if local storage is unavailable. */ }
      clearAuthAttempt();
      try {
        if (session?.user.id) window.localStorage.removeItem(`home-twice:claim-offer-skipped:${session.user.id}`);
      } catch { /* App-owned browser markers are best-effort if storage is unavailable. */ }
      navigate("/account-deleted", { replace: true });
    } catch (deleteError) {
      setError(friendlyErrorMessage(
        deleteError,
        "account",
        "Account deletion did not finish. Please try again or contact support; some associated data may already have been removed.",
      ));
    } finally {
      setIsDeletingAccount(false);
    }
  }

  if (placement === "header") {
    return (
      <div className="account-actions">
        <button type="button" className="account-sign-out" onClick={() => void handleSignOut()} disabled={isSigningOut || isDeletingAccount}>
          {isSigningOut ? "Signing out…" : "Sign out"}
        </button>
        {error && <p className="account-error" role="alert">{error}</p>}
      </div>
    );
  }

  const sectionTitleId = `account-section-title-${placement}`;
  const confirmationId = `account-delete-confirmation-${placement}`;
  const deleteTitleId = `account-delete-title-${placement}`;

  return (
    <section
      className={`account-section account-section-${placement}`}
      aria-labelledby={sectionTitleId}
    >
      <h2 className="account-section-title" id={sectionTitleId}>Your account</h2>
      <nav className="account-section-links" aria-label="Your account">
        <button
          type="button"
          className="account-delete-link"
          onClick={() => { setError(null); setShowDeleteForm((shown) => !shown); setDeleteConfirmation(""); }}
          disabled={isSigningOut || isDeletingAccount}
        >
          Delete my account
        </button>
      </nav>
      {showDeleteForm && (
        <section className="account-delete-panel" aria-labelledby={deleteTitleId}>
          <h2 id={deleteTitleId}>Permanently delete your account?</h2>
          <p>This removes your account, recordings, audio files, and share links. This cannot be undone.</p>
          <label htmlFor={confirmationId}>Type DELETE to confirm</label>
          <input
            id={confirmationId}
            name={confirmationId}
            className="email-input"
            autoComplete="off"
            autoCapitalize="characters"
            value={deleteConfirmation}
            onChange={(event) => setDeleteConfirmation(event.target.value)}
            disabled={isDeletingAccount}
          />
          <div className="account-delete-actions">
            <button
              type="button"
              className="destructive-action"
              onClick={() => void handleDeleteAccount()}
              disabled={deleteConfirmation !== "DELETE" || isDeletingAccount}
            >
              {isDeletingAccount ? "Deleting account…" : "Delete account permanently"}
            </button>
            <button
              type="button"
              className="secondary-action"
              onClick={() => { setShowDeleteForm(false); setDeleteConfirmation(""); setError(null); }}
              disabled={isDeletingAccount}
            >
              Cancel
            </button>
          </div>
        </section>
      )}
      {error && <p className="account-error" role="alert">{error}</p>}
    </section>
  );
}
